/**
 * <products-scroller> — Splide carousel of product cards.
 *
 * The slider options mirror the Media Tiles section (fractional slides per
 * view, auto-scroll, per-breakpoint settings) but the scripts are fetched on
 * demand: nothing loads until the scroller nears the viewport, and the
 * auto-scroll extension only loads when a section actually asks for it.
 *
 * A fractional slides-per-view (2.3, say) is done the way Media Tiles does it:
 * floor the count for perPage and give the track a right padding of the
 * leftover fraction, so the peeked slide fills exactly what is left over.
 */
if (!customElements.get('products-scroller')) {
  const BREAKPOINT = 768;

  const loaders = new Map();

  /**
   * Always appends its own tag rather than waiting on a matching one already in
   * the document: a page can carry several sections that each queue splide.min.js,
   * and a tag that has already executed never fires load again, which would hang
   * this promise forever. A second tag for the same URL is served from cache and
   * only re-runs a UMD bundle that reassigns its own global, so the duplicate
   * costs nothing. Memoised per src, so this element never queues one twice.
   */
  function loadScript(src) {
    if (loaders.has(src)) return loaders.get(src);

    const loader = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      script.src = src;
      document.head.appendChild(script);
    });

    loaders.set(src, loader);
    return loader;
  }

  class ProductsScroller extends HTMLElement {
    connectedCallback() {
      if (this.dataset.scrollerInit) return;
      this.dataset.scrollerInit = 'true';

      this.d = this.dataset;
      this.items = Array.from(this.querySelectorAll('.products-scroller--item'));
      if (this.items.length < 1) return;

      this.observe();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('resize', this.onResize);
      this.destroySlider();
    }

    observe() {
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '300px 0px' }
      );

      this.observer.observe(this);
    }

    load() {
      const wantsAutoScroll = this.d.autoscrollDesktop === 'true' || this.d.autoscrollMobile === 'true';

      const splideReady = window.Splide ? Promise.resolve() : loadScript(this.d.splideSrc);
      const autoScrollReady = () => {
        if (!wantsAutoScroll) return null;
        return window.splide?.Extensions?.AutoScroll ? null : loadScript(this.d.autoscrollSrc);
      };

      splideReady
        .then(autoScrollReady)
        .then(() => {
          this.update();

          this.onResize = () => this.update();
          window.addEventListener('resize', this.onResize);

          if (window.Shopify?.designMode) {
            this.addEventListener('shopify:section:load', () => this.update());
          }
        })
        // Scripts unavailable: the cards stay a plain scrollable row.
        .catch(() => this.classList.add('products-scroller--static'));
    }

    get isDesktop() {
      return window.innerWidth >= BREAKPOINT;
    }

    get perPage() {
      return parseFloat(this.isDesktop ? this.d.desktopPerPage : this.d.mobilePerPage) || 1;
    }

    get autoScroll() {
      return this.isDesktop ? this.d.autoscrollDesktop === 'true' : this.d.autoscrollMobile === 'true';
    }

    get autoScrollSpeed() {
      const raw = this.isDesktop ? this.d.autoscrollSpeedDesktop : this.d.autoscrollSpeedMobile;
      return parseFloat(raw) || 1;
    }

    /**
     * Falls back to the single data-gap so a section that has not split its
     * gap per breakpoint (products-scroller, say) keeps working unchanged.
     */
    get gap() {
      return (this.isDesktop ? this.d.desktopGap : this.d.mobileGap) || this.d.gap || '12px';
    }

    get scrollPadding() {
      const value = parseInt(this.isDesktop ? this.d.desktopScrollPadding : this.d.mobileScrollPadding, 10);
      return value > 0 ? `${value}px` : '0px';
    }

    /** Re-initialise only when the breakpoint actually changes the setup. */
    update() {
      const state = `${this.perPage}:${this.autoScroll}:${this.gap}`;
      if (state === this.currentState) return;
      this.currentState = state;

      this.initSlider();
    }

    peekPadding(perPage) {
      let fraction = perPage % 1;
      if (fraction === 0) {
        // A whole-number perPage normally means no peek at all. When the
        // section wants one anyway (e.g. a "View all" tile that should stay
        // partly visible without scrolling), fake a small fractional perPage
        // so the same padding math still produces a peek.
        if (this.d.forcePeek !== 'true') return '0%';
        fraction = 0.15;
        perPage = Math.floor(perPage) + fraction;
      }
      // padding = fraction / perPage * 100, derived so the floored slides plus
      // the peek fill the container exactly.
      return `${((fraction / perPage) * 100).toFixed(3)}%`;
    }

    initSlider() {
      this.destroySlider();

      const perPage = this.perPage;
      const perPageFloor = Math.max(1, Math.floor(perPage));
      const hasPeek = perPage % 1 !== 0 || this.d.forcePeek === 'true';
      const autoScroll = this.autoScroll;
      const loop = this.d.loop === 'true';
      const showArrows = this.d.showArrows === 'true';

      // Auto-scroll has to loop. Otherwise fall back to slide + rewind when a
      // peek is showing, since a loop cannot align a partial slide.
      const useLoop =
        autoScroll || (loop && !hasPeek && this.items.length >= perPageFloor * 2 + 1);

      this.classList.add('splide');
      this.querySelector('.products-scroller--track').classList.add('splide__track');
      this.querySelector('.products-scroller--list').classList.add('splide__list');
      this.items.forEach((item) => item.classList.add('splide__slide'));

      const options = {
        type: useLoop ? 'loop' : 'slide',
        rewind: !useLoop && loop,
        perPage: perPageFloor,
        perMove: 1,
        gap: this.gap,
        padding: { left: this.scrollPadding, right: this.peekPadding(perPage) },
        arrows: false,
        pagination: this.d.showDots === 'true',
        autoplay: autoScroll ? false : this.d.autoplay === 'true',
        interval: 4000,
        pauseOnHover: true,
        drag: autoScroll ? 'free' : true
      };

      if (autoScroll) {
        options.autoScroll = {
          speed: this.autoScrollSpeed,
          pauseOnHover: true,
          pauseOnFocus: true,
        };
      }

      const AutoScroll = window.splide?.Extensions?.AutoScroll;
      this.splide = new Splide(this, options).mount(
        autoScroll && AutoScroll ? { AutoScroll } : {}
      );

      const prev = this.querySelector('.products-scroller--prev');
      const next = this.querySelector('.products-scroller--next');
      if (prev && next) {
        prev.onclick = () => this.splide.go('<');
        next.onclick = () => this.splide.go('>');
        // Auto-scroll runs continuously; arrows would only fight it.
        const visible = showArrows && !autoScroll;
        prev.hidden = !visible;
        next.hidden = !visible;

        // A looping or rewinding slider always has somewhere to go, so its
        // arrows stay live. A plain slide runs out, and the arrow for the end
        // it has reached is disabled rather than hidden, so the pair keeps its
        // position. Controller.getEnd() is Splide's own last reachable index,
        // which already accounts for perPage and the peek padding.
        if (visible && !useLoop && !options.rewind) {
          const syncArrows = (index) => {
            const current = index ?? this.splide.index;
            const end = this.splide.Components.Controller.getEnd();
            prev.disabled = current <= 0;
            next.disabled = current >= end;
          };

          // 'move' fires before splide.index is updated, so read the incoming
          // index off the event; everything else can read it back.
          this.splide.on('move', (newIndex) => syncArrows(newIndex));
          this.splide.on('moved updated refresh resized', () => syncArrows());
          syncArrows();
        }
      }

      this.classList.add('is-slider');
    }

    destroySlider() {
      if (!this.splide) return;

      this.splide.destroy(true);
      this.splide = null;

      this.querySelector('.products-scroller--prev')?.removeAttribute('disabled');
      this.querySelector('.products-scroller--next')?.removeAttribute('disabled');

      this.classList.remove('splide', 'splide--slide', 'splide--loop', 'splide--ltr', 'is-slider');
      this.querySelector('.products-scroller--track')?.classList.remove('splide__track');
      this.querySelector('.products-scroller--list')?.classList.remove('splide__list');
      this.items.forEach((item) => {
        item.classList.remove('splide__slide');
        item.style.width = '';
      });
    }
  }

  customElements.define('products-scroller', ProductsScroller);
}
